import React, { useState } from 'react';
import { mangaAspectClass } from '../../constants/manga';

export interface MangaStoryScrollPage {
  page_number: number;
  image_url: string;
}

export interface MangaStoryScrollCta {
  text: string;
  url: string;
}

interface MangaStoryScrollProps {
  pages: MangaStoryScrollPage[];
  /**
   * '1:1' | '9:16' | '16:9'。未指定はスクエア扱い。
   *
   * 読み込み前のプレースホルダ枠にだけ使う。過去のプロジェクトは宣言値と
   * 実画像の比率が食い違う（例: 9:16 宣言で実体は 1024x1024）ことがあるので、
   * 読み込み後は実画像の比率に従わせて切り取りを起こさない。
   */
  aspectRatio?: string;
  /** CTA が無い（cta_url 未設定）ときはボタンを出さない */
  cta?: MangaStoryScrollCta | null;
  title?: string;
  className?: string;
}

interface StoryPageProps {
  page: MangaStoryScrollPage;
  /** 読み込み完了までのプレースホルダ枠クラス */
  placeholderClass: string;
}

/**
 * 1 ページぶんの画像。読み込みが確定するまでは宣言比率の枠を出しておき、
 * onLoad / onError で枠を外して実画像の高さに任せる。
 */
const StoryPage: React.FC<StoryPageProps> = ({ page, placeholderClass }) => {
  const [isSettled, setIsSettled] = useState(false);
  const wrapperClass = [
    'w-full overflow-hidden rounded-md bg-warm-card',
    isSettled ? '' : placeholderClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClass} data-testid="manga-story-page">
      <img
        src={page.image_url}
        alt={`${page.page_number}ページ目`}
        loading="lazy"
        onLoad={() => setIsSettled(true)}
        onError={() => setIsSettled(true)}
        className="block h-auto w-full"
      />
    </div>
  );
};

/**
 * 漫画を縦スクロールで読ませる表示コンポーネント。
 *
 * 完了画面のプレビューモーダルと公開ページ（/m/:token）で共用する。
 * データ取得は行わない純表示コンポーネントなので、呼び出し側で
 * ページ配列と CTA を確定させてから渡すこと。
 */
const MangaStoryScroll: React.FC<MangaStoryScrollProps> = ({
  pages,
  aspectRatio,
  cta,
  title,
  className,
}) => {
  const placeholderClass = mangaAspectClass(aspectRatio);
  const hasCta = Boolean(cta && cta.url && cta.text);

  return (
    <section
      aria-label={title ? `${title} の漫画` : '漫画'}
      className={`mx-auto w-full max-w-[480px] ${className ?? ''}`}
    >
      <div className="space-y-2">
        {pages.map(page => (
          // 画像 URL を key に含める。同じページ番号のまま画像が差し替わった
          // （再生成中にプレビューを開いたままにした等）ときに読み込み済み状態を
          // 引き継がせず、新しい画像でプレースホルダ枠からやり直させる。
          <StoryPage
            key={`${page.page_number}:${page.image_url}`}
            page={page}
            placeholderClass={placeholderClass}
          />
        ))}
      </div>

      {hasCta && cta && (
        <a
          href={cta.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 block w-full rounded-lg bg-primary px-6 py-4 text-center text-base font-bold text-white transition-colors duration-200 hover:bg-primary-hover"
        >
          {cta.text}
        </a>
      )}
    </section>
  );
};

export default MangaStoryScroll;
